import { scopedApi } from '@/session/scopedClients';
import { GLOBAL_SCOPE } from '@/session/scopedTransport';
import { EndpointResponse } from '@/types/endpoints';
import { isRecord } from '@/utils/dataUtils';
import { utils } from '../../utils';
import type { ReadRequestOptions } from '../requestOptions';

export type OrganizationCreateStatus = {
  enabled: boolean;
};

// Reads the operator-controlled runtime switch that astur_organization_srv keeps
// in Redis (flipped from a running pod with `server_app --disable-create-orgs` /
// `--enable-create-orgs`). Authenticated-global like createOrganization: the
// switch is platform-wide and the route is registered Global, so the operation
// owns GLOBAL_SCOPE (globalEndpoints convention).
//
// The service restricts this route to the same superuser / acenda_the_management
// roles that may create an organization, so a 403 here means "this reader may not
// see the switch" - never "creation is off". Callers must keep those apart.
export const getOrganizationCreateStatus = async (options: ReadRequestOptions): Promise<EndpointResponse<OrganizationCreateStatus>> => {
  try {
    const response = await scopedApi(GLOBAL_SCOPE).get<EndpointResponse<OrganizationCreateStatus>>('organization/create_status', options);
    const body = response.data;
    if (body.isError) return body;

    // This drives a banner that tells staff creation is off, so a body that is
    // not the documented `{ enabled: boolean }` must surface as a failure. A
    // missing flag collapsing to falsy would read as a deliberate disable.
    if (!isRecord(body.result) || typeof body.result.enabled !== 'boolean') {
      throw new Error('Organization creation status response did not include a boolean enabled flag');
    }

    return body;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
